import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Clock, UserPlus, Save, Search } from "lucide-react";
import MilitaryVerification from "@/components/MilitaryVerification";

export default function CaregiverDashboard() {
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({ patient_name: "", patient_email: "" });
  const [edits, setEdits] = useState({});

  const load = () => {
    api.get("/caregiver/patients").then(({ data }) => setPatients(data)).catch(() => {});
  };
  useEffect(load, []);

  const apply = async () => {
    if (!form.patient_name || !form.patient_email) return toast.error("Enter patient name and email");
    try {
      await api.post("/caregiver/apply", form);
      toast.success("Verification email sent to your patient. They must confirm to link.");
      setForm({ patient_name: "", patient_email: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveEdit = async (bookingId) => {
    const e = edits[bookingId];
    if (!e) return;
    try {
      await api.put(`/bookings/${bookingId}`, e);
      toast.success("Booking updated");
      setEdits((p) => { const n = { ...p }; delete n[bookingId]; return n; });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <MilitaryVerification />
      <div className="card-metal p-6">
        <h2 className="font-heading font-black text-lg flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-[#9b111e]" /> Link a Patient
        </h2>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div><Label>Patient full name</Label><Input className="mt-1 bg-white" data-testid="cg-patient-name"
            value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} /></div>
          <div><Label>Patient email</Label><Input type="email" className="mt-1 bg-white" data-testid="cg-patient-email"
            value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} /></div>
          <button onClick={apply} className="btn-red-3d" data-testid="cg-apply-btn">Send Verification</button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Your patient will receive an email to verify your name and email before you can manage their bookings.
        </p>
      </div>

      <div>
        <h2 className="font-heading font-black text-xl mb-4">My Patients</h2>
        {patients.length === 0 && <p className="text-slate-500">No linked patients yet.</p>}
        <div className="space-y-5">
          {patients.map((p) => (
            <div key={p.id} className="card-metal p-5" data-testid={`patient-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-black text-lg">{p.patient_name}</p>
                  <p className="text-sm text-slate-500">{p.patient_email}</p>
                </div>
                <span className={`badge-tier ${p.status === "verified" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {p.status === "verified" ? <><ShieldCheck className="w-3 h-3 inline" /> Verified</> : "Pending"}
                </span>
              </div>

              {p.status === "verified" && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold flex items-center gap-1 text-sm"><Clock className="w-4 h-4 text-[#9b111e]" /> Bookings</h4>
                    <Link to="/search" className="text-sm text-[#9b111e] font-semibold flex items-center gap-1">
                      <Search className="w-4 h-4" /> Book new
                    </Link>
                  </div>
                  {(!p.bookings || p.bookings.length === 0) && <p className="text-sm text-slate-500">No bookings.</p>}
                  <div className="space-y-2">
                    {(p.bookings || []).map((b) => {
                      const e = edits[b.id] || {};
                      return (
                        <div key={b.id} className="border border-slate-200 rounded-lg p-3 bg-white flex flex-wrap gap-3 items-end" data-testid={`cg-booking-${b.id}`}>
                          <div className="flex-1 min-w-[160px]">
                            <p className="font-semibold">{b.service} — {b.provider_name}</p>
                            <p className="text-xs text-slate-500">${b.price} · {b.payment_status}</p>
                          </div>
                          <div>
                            <Label className="text-xs">Reschedule</Label>
                            <Input type="datetime-local" className="mt-1 bg-white text-sm" data-testid={`cg-reschedule-${b.id}`}
                              defaultValue={b.scheduled_at?.slice(0, 16)}
                              onChange={(ev) => setEdits((prev) => ({ ...prev, [b.id]: { ...e, scheduled_at: ev.target.value } }))} />
                          </div>
                          <div>
                            <Label className="text-xs">Status</Label>
                            <Select defaultValue={b.status} onValueChange={(v) => setEdits((prev) => ({ ...prev, [b.id]: { ...(prev[b.id] || {}), status: v } }))}>
                              <SelectTrigger className="mt-1 bg-white text-sm w-36" data-testid={`cg-status-${b.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <button onClick={() => saveEdit(b.id)} className="btn-silver-3d !py-2 !px-3" data-testid={`cg-save-${b.id}`}>
                            <Save className="w-4 h-4 text-[#9b111e]" /><span className="text-engraved text-xs">Save</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
